/**
 * «HVEM HAR TILGANG» (D3, QA13 #107, 253.25) — `GET /v1/performances/:id/access` for eieren og
 * `GET /v1/advance-shares/:performanceId/access` for motparten i en delt advance.
 *
 * Lista regnes av den samme `can()` som avgjør tilgangen, kjørt for hver person, så den aldri kan
 * være uenig med reglene. Den har to deler, som delingsvinduet i Google Docs:
 *
 *  * én linje per ORGANISASJON med hvor mange som kan åpne programposten, som kan åpnes for navnene;
 *  * navngitte linjer for eksterne, inviterte, lenkepersoner og reisefølget med innlogging, med
 *    HVORDAN de fikk tilgang og HVA de kan gjøre.
 *
 * Festivalen ser navnene i byråets organisasjon (253.25). Byrået ser festivalens antall, men ikke
 * navnene — det er ikke avgjort, og den smaleste varianten er bygget (spørsmål i rapporten).
 */
import { z } from 'zod';

import { ACTOR_ROLES } from '../enums.js';
import { wireInstant } from './wire-time.js';

export const ACCESS_ORGANIZATION_SIDES = ['owner', 'counterpart'] as const;

export const accessMemberSchema = z
  .object({
    personId: z.uuid(),
    displayName: z.string(),
    role: z.enum(ACTOR_ROLES),
  })
  .meta({ id: 'AccessMember' });
export type AccessMember = z.infer<typeof accessMemberSchema>;

export const accessOrganizationSchema = z
  .object({
    organizationId: z.uuid(),
    organizationName: z.string(),
    /** Eieren av programposten, eller motparten i den delte advancen. */
    side: z.enum(ACCESS_ORGANIZATION_SIDES),
    /** Organisasjonen den innloggede er i. */
    mine: z.boolean(),
    /** Hvor mange som kan åpne programposten, regnet av `can()`. */
    count: z.number().int().nonnegative(),
    /** Navnene, når den innloggede får se dem; ellers null. */
    members: z.array(accessMemberSchema).nullable(),
  })
  .meta({ id: 'AccessOrganization' });
export type AccessOrganization = z.infer<typeof accessOrganizationSchema>;

/**
 * HVORDAN personen fikk tilgang. `external`: en ekstern med innlogging, `invited`: invitert og ikke
 * logget inn ennå, `link`: en personlig lenke uten innlogging, `touring_party`: står i reisefølget
 * og har innlogging (artistvisningen, 0380).
 */
export const ACCESS_HOWS = ['external', 'invited', 'link', 'touring_party'] as const;
export type AccessHow = (typeof ACCESS_HOWS)[number];

/** Hva delingen gjelder: programposten selv, hele årgangen eller avdelingen. */
export const ACCESS_VIAS = ['performance', 'edition', 'department', 'party'] as const;

export const accessPersonSchema = z
  .object({
    /** Delingens id, eller reisefølgeradens. Stabil nøkkel for lista. */
    key: z.uuid(),
    personId: z.uuid(),
    displayName: z.string(),
    how: z.enum(ACCESS_HOWS),
    via: z.enum(ACCESS_VIAS),
    /** Nivået på delingen; null for reisefølget, som ser det byrået har slått på. */
    permission: z.enum(['view', 'edit']).nullable(),
    /** Fanene delingen dekker; null er hele programposten (aldri kontrakt og økonomi). */
    scopes: z.array(z.string()).nullable(),
    expiresAt: wireInstant.nullable(),
    /** F17: organisasjonen som delte videre, når det ikke er eieren. */
    grantedByOrganizationName: z.string().nullable(),
  })
  .meta({ id: 'AccessPerson' });
export type AccessPerson = z.infer<typeof accessPersonSchema>;

export const performanceAccessSchema = z
  .object({
    performanceId: z.uuid(),
    organizations: z.array(accessOrganizationSchema),
    people: z.array(accessPersonSchema),
  })
  .meta({ id: 'PerformanceAccess' });
export type PerformanceAccess = z.infer<typeof performanceAccessSchema>;
